import * as Sentry from "@sentry/react";
import { notification } from "antd";
import Helper from "../../Helper";
import { globalAppConfig } from "../../config";
import { MapLayer } from "../../models/MapLayer";
import PlotAPI from "../../plotAPI";

export function getProjectMapLayers(projectToken: string, subdomain: string): Promise<any[]> {
    const url = `${Helper.baseUrlAltSubdomain(subdomain)}/projects/${projectToken}/layers`;
    return Helper.plotFetch(url)
        .then((res) => {
            if (res.ok) {
                return res.json();
            }

            throw new Error("Failed to get project layers");
        })
        .then((res) => {
            res.forEach((layer) => {
                {
                    layer.features.sort((a, b) => {
                        return a.properties.plot.areas_index - b.properties.plot.areas_index;
                    });
                }
            });
            return res;
        })
        .catch((err) => {
            Sentry.captureException(err);
        });
}
export function getActiveDeliveries(
    projectToken: string,
    subdomain: string = ""
): Promise<{ id: number; features: { id: number; uuid: string; active_deliveries: any[] }[] }[]> {
    let url = "/projects/" + projectToken + "/deliveries/locations";
    if (subdomain && subdomain.length > 0) {
        url = Helper.baseUrlAltSubdomain(subdomain) + url;
    }
    return Helper.plotFetch(url)
        .then((res) => {
            if (res.ok) {
                return res.json();
            }
            throw new Error("Failed to get project deliveries");
        })
        .catch((err) => {
            Sentry.captureException(err);
        });
}

export async function getSingleMapObjectInformation(project_token: string, map_object_id: string): Promise<object[]> {
    const url = "/projects/" + project_token + "/layers/";
    return Helper.plotFetch(url + "?f=geojson")
        .then((res) => {
            if (res.ok) {
                return res.json();
            }
            throw new Error("Failed to get project layers");
        })
        .then((res) => {
            const layerIndex = res.findIndex((layer) => layer.features.find((area) => area.id === map_object_id));
            const area = res[layerIndex].features.find((area) => area.id === map_object_id);
            return area;
        })
        .catch((error) => {
            Sentry.captureException(error);
        });
}

export function getDeliveriesForLocation(activeDeliveries, object_id: string, index: number) {
    const deliveryArea = activeDeliveries[index].features.find((area) => {
        return area.uuid === object_id;
    });

    return deliveryArea;
}

export function getDirections(
    token: string,
    starPoint: { lon: number; lat: number },
    endPoint: { lon: number; lat: number }
): Promise<object[]> {
    const url = `${Helper.baseUrlWithPort()}/projects/${token}/map_integrations/mapbox_directions`;
    const body = {
        start_point: starPoint,
        end_point: endPoint,
    };

    const options = {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
    };
    return Helper.plotFetch(url, options).then((res) => {
        if (res.ok) {
            return res.json();
        }
        throw new Error("Failed to get directions");
    });
}

export function getMapObjectImage(mapObject): Promise<string> {
    //As of right now only points can be used to generate images
    function returnCoordinates(object) {
        let coordinates = [];
        if (object.geometry.type === "Point") {
            coordinates = object.geometry.coordinates;
        } else if (object.geometry.type === "LineString") {
            coordinates = object.geometry.coordinates[0];
        } else {
            coordinates = object.geometry.coordinates[0][0];
        }
        return coordinates;
    }
    const mapMarkerIcon = "https://i.ibb.co/fdcLfgH/8324266-ui-essential-app-location-map-icon-2.png";
    const zoom = 15;
    const pitch = 0;
    const style = "satellite-streets-v11";
    const mapObjectCoordinates = returnCoordinates(mapObject);
    const imageSize = "300x300";
    const url =
        "https://api.mapbox.com/styles/v1/mapbox/" +
        style +
        "/static/url-" +
        encodeURIComponent(mapMarkerIcon) +
        "(" +
        mapObjectCoordinates[0] +
        "," +
        mapObjectCoordinates[1] +
        ")/" +
        mapObjectCoordinates[0] +
        "," +
        mapObjectCoordinates[1] +
        "," +
        zoom +
        "," +
        pitch +
        "/" +
        imageSize +
        "?access_token=" +
        globalAppConfig.MAP_BOX_ACCESS_TOKEN;
    return Helper.plotFetch(url)
        .then((res) => {
            if (res.ok) {
                return res.blob();
            }

            throw new Error("Failed to get location image");
        })
        .then((res) => {
            return URL.createObjectURL(res);
        })
        .catch(() => {
            return "";
        });
}

export function findLayerIndex(array: any[], val: string) {
    return array.findIndex((layer) => layer.features.find((area) => area.id === val));
}
export function findAreaIndex(area, val: string) {
    return area[this.findLayerIndex(area, val)].features.findIndex((area) => area.id === val);
}
export function findObjectById(layers, id: string) {
    const result = layers[this.findLayerIndex(layers, id)].features[this.findAreaIndex(layers, id)];
    return result;
}

export function saveMapLayers(
    map_layers: any[],
    currentProjectInfo: any,
    tagged_teams: number[] = [],
    area_updated?: any
): Promise<any> {
    let error;
    return Promise.all(
        map_layers.map((layer) => {
            const url = `/projects/${currentProjectInfo.unique_token}/layers/${layer.properties.id}`;
            const options = {
                method: "put",
                body: JSON.stringify({
                    layer: layer,
                    area_updated:
                        layer.properties.id === area_updated?.properties?.plot?.layer_id &&
                        area_updated.properties.plot.id,
                    tagged_teams: tagged_teams,
                }),
            };
            return Helper.plotFetch(url, options, true)
                .then((res) => {
                    return Promise.resolve(res.json());
                })
                .catch((err) => {
                    return Promise.resolve(err.json());
                });
        })
    )
        .then((response) => {
            if (response.some((res) => res.error)) {
                error = response.find((res) => res.error);
                return Promise.reject(error);
            }
            const jobsiteEntrance = map_layers
                .flatMap((layer) => layer.features)
                .find((area) => {
                    return area.properties.plot.area_name === "Jobsite Entrance";
                });
            if (
                jobsiteEntrance.geometry.coordinates[0] !== currentProjectInfo.lon ||
                jobsiteEntrance.geometry.coordinates[1] !== currentProjectInfo.lat
            ) {
                const projectInfo = currentProjectInfo;
                projectInfo.lon = jobsiteEntrance.geometry.coordinates[0];
                projectInfo.lat = jobsiteEntrance.geometry.coordinates[1];
                return PlotAPI.putProjectInfo(currentProjectInfo.unique_token, projectInfo);
            }
            return response;
        })
        .catch((err) => {
            return Promise.reject(err);
        });
}

export function destroyMapLayer(projectToken: string, layerId: number): Promise<any> {
    const url = `/projects/${projectToken}/layers/${layerId}`;
    const options = {
        method: "delete",
    };
    return Helper.plotFetch(url, options, true).then((res) => {
        if (res.ok) {
            return res.json();
        }
        throw new Error("Failed to get destroy map layer");
    });
}

export function archiveMapObject(currentProjectInfo: any, archive_area?: any): Promise<any> {
    const url = `/projects/${currentProjectInfo.unique_token}/layers/${archive_area.properties.plot.layer_id}/areas/${archive_area.properties.plot.id}`;
    const options = {
        method: "delete",
    };
    return Promise.resolve(
        Helper.plotFetch(url, options).then((res) => {
            if (res.ok) {
                return res.json();
            }
            throw new Error("Failed to get archive map object");
        })
    );
}

export function destroyMapArea(projectToken: string, layerId: number, areaId: number): Promise<any> {
    const url = `/projects/${projectToken}/layers/${layerId}/areas/${areaId}`;
    const options = {
        method: "delete",
    };
    return Helper.plotFetch(url, options, true)
        .then((res) => {
            if (res.ok) {
                notification.success({
                    title: "Layer Deleted",
                    description: "The layer has been deleted",
                });
                return res.json();
            }

            throw new Error("Failed to get destroy map area");
        })
        .catch(() => {
            return null;
        });
}

export function geocoder(placeType, value) {
    const mapboxAPIurl = "https://api.mapbox.com/geocoding/v5/mapbox.places/";
    const queryURL = mapboxAPIurl + value + ".json?" + "&access_token=" + globalAppConfig.MAP_BOX_ACCESS_TOKEN;
    Helper.plotFetch(queryURL, {}, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
        })
        .then((response) => {
            return response;
        })
        .catch((err) => {
            Sentry.captureException(err);
        });
}

export const fetchProjectMapLayers = async (token: string) => {
    const url = `${Helper.baseUrlWithPort()}/projects/${token}/map_layers`;
    const resp = await Helper.plotFetch(url);
    return resp.json();
};
export const parseWtmsUrl = async (token: string, wtmsUrl: string) => {
    const url = `${Helper.baseUrlWithPort()}/projects/${token}/map_integrations/wmts_layers`;
    const body = {
        wmts_url: wtmsUrl,
    };
    const resp = await Helper.plotFetch(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
    });
    return resp.json().then((data) => data);
};

export const verifyDroneDeployApiKey = async (token: string, key: string) => {
    const url = `${Helper.baseUrlWithPort()}/projects/${token}/map_integrations/drone_deploy_verify_key`;
    const body = {
        dronedeploy_key: key,
    };

    const resp = await Helper.plotFetch(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
    });
    return resp.json();
};

export const getDroneDeployProjects = async (token: string, startCursor: string | null = null) => {
    const url = `${Helper.baseUrlWithPort()}/projects/${token}/map_integrations/drone_deploy_projects${startCursor ? `?start_cursor=${startCursor}` : ""}`;
    const resp = await Helper.plotFetch(url);
    return resp.json();
};

export const getDroneDeployProjectLayers = async (token: string, dronedeployProjectId: string, cursor?: string) => {
    const url = `${Helper.baseUrlWithPort()}/projects/${token}/map_integrations/drone_deploy_project_layers`;
    const body = {
        drone_deploy_project_id: dronedeployProjectId,
        cursor: cursor,
    };

    const resp = await Helper.plotFetch(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
    });
    if (resp.ok) {
        return resp.json();
    } else {
        throw new Error("Failed to get DroneDeploy project layers");
    }
};

export const createMapLayer = async (token: string, layer: MapLayer) => {
    const url = `${Helper.baseUrlWithPort()}/projects/${token}/map_layers`;

    const resp = await Helper.plotFetch(url, {
        method: "POST",
        body: JSON.stringify({ layer: layer.toApiObject() }),
        headers: { "Content-Type": "application/json" },
    });
    return resp.json();
};

export const updateMapLayer = async (token: string, layer: MapLayer) => {
    const url = `${Helper.baseUrlWithPort()}/projects/${token}/map_layers/${layer.id}`;

    const resp = await Helper.plotFetch(url, {
        method: "PUT",
        body: JSON.stringify({ layer: layer.toApiObject() }),
        headers: { "Content-Type": "application/json" },
    });
    return resp.json();
};

export const deleteMapLayer = async (token: string, layerId: number) => {
    const url = `${Helper.baseUrlWithPort()}/projects/${token}/map_layers/${layerId}`;

    const resp = await Helper.plotFetch(url, {
        method: "DELETE",
    });
    return resp.json();
};
